import type { T_JsonValue } from "@lattice/core-lib/types/chat-protocol";
import type { T_ChatV8TableWidget } from "@lattice/core-lib/types/chat-v8";

import type {
  T_MarkdownTableAlignment,
  T_ParsedMarkdownTable,
} from "./markdown-table-segments";

const MAX_VISIBLE_TABLE_ROWS = 12;

export type T_TablePresentation = {
  copyMarkdown: string;
  data: T_ChatV8TableWidget;
  hiddenRowCount: number;
};

export type T_TableColumnLayoutInput = {
  /** Card inner width available for the grid. */
  availableWidth: number;
  /** Average body glyph width in px (device-calibrated by the caller). */
  charWidth: number;
  /** Per-cell horizontal padding (both sides summed by the fn). */
  cellPaddingHorizontal: number;
  /** Semibold header glyphs run slightly wider. */
  headerCharWidth: number;
  maxColumnWidth: number;
  minColumnWidth: number;
};

export type T_TableColumnLayout = {
  columnWidths: number[];
  requiresHorizontalScroll: boolean;
};

/**
 * Content-driven column widths (James's design, 2026-07-13): estimate each
 * column from its longest content line, clamp to [min, max] — content only
 * wraps once a column hits max (or on an explicit line break) — then either
 * distribute leftover width so the table fills the card (no dead right
 * side) or overflow into horizontal scroll when content genuinely needs it.
 */
export function computeTableColumnLayout(
  data: T_ChatV8TableWidget,
  input: T_TableColumnLayoutInput,
): T_TableColumnLayout {
  const visibleRows = data.rows.slice(0, MAX_VISIBLE_TABLE_ROWS);
  const estimates = data.columns.map((column) => {
    const headerLen = longestLine(column.label);
    let contentLen = 0;
    for (const row of visibleRows) {
      const cell = formatTableCell(row[column.key]);
      const len = longestLine(cell);
      if (len > contentLen) contentLen = len;
    }
    const estimate =
      Math.max(
        headerLen * input.headerCharWidth,
        contentLen * input.charWidth,
      ) +
      input.cellPaddingHorizontal * 2;
    return Math.min(
      input.maxColumnWidth,
      Math.max(input.minColumnWidth, estimate),
    );
  });

  const total = estimates.reduce((sum, width) => sum + width, 0);
  if (total >= input.availableWidth) {
    return { columnWidths: estimates, requiresHorizontalScroll: true };
  }

  // Fill the card: hand leftover width to columns proportionally to their
  // content-driven estimates, so prose columns absorb most of it.
  const leftover = input.availableWidth - total;
  const columnWidths = estimates.map(
    (width) => width + (leftover * width) / total,
  );
  return { columnWidths, requiresHorizontalScroll: false };
}

function longestLine(text: string): number {
  let longest = 0;
  for (const line of text.split("\n")) {
    if (line.length > longest) longest = line.length;
  }
  return longest;
}

export function prepareTablePresentation(
  data: T_ChatV8TableWidget,
  alignments: T_MarkdownTableAlignment[] = [],
): T_TablePresentation {
  const visibleRows = data.rows.slice(0, MAX_VISIBLE_TABLE_ROWS);
  return {
    copyMarkdown: serializeTableWidget(data, alignments),
    data: { ...data, rows: visibleRows },
    hiddenRowCount: data.rows.length - visibleRows.length,
  };
}

export function prepareMarkdownTablePresentation(
  table: T_ParsedMarkdownTable,
): T_TablePresentation {
  const columns = table.headerCells.map((label, index) => ({
    key: `col-${index}`,
    label,
  }));
  const rows = table.rows.map((cells) => {
    const row: Record<string, string> = {};
    columns.forEach((column, index) => {
      row[column.key] = cells[index] ?? "";
    });
    return row;
  });

  return prepareTablePresentation({ columns, rows }, table.alignments);
}

export function formatTableCell(value: T_JsonValue | undefined): string {
  if (typeof value === "string" || typeof value === "number") {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "✓" : "—";
  }
  return "—";
}

function serializeTableWidget(
  data: T_ChatV8TableWidget,
  alignments: T_MarkdownTableAlignment[],
): string {
  const header = serializeMarkdownRow(
    data.columns.map((column) => column.label),
  );
  const delimiter = serializeMarkdownRow(
    data.columns.map((_, index) => alignmentMarker(alignments[index] ?? null)),
  );
  const rows = data.rows.map((row) =>
    serializeMarkdownRow(
      data.columns.map((column) => formatTableCell(row[column.key])),
    ),
  );
  return [header, delimiter, ...rows].join("\n");
}

function serializeMarkdownRow(cells: string[]): string {
  return `| ${cells.map(escapeMarkdownCell).join(" | ")} |`;
}

function escapeMarkdownCell(cell: string): string {
  return cell
    .replace(/\\/g, "\\\\")
    .replace(/\|/g, "\\|")
    .replace(/\r?\n/g, "<br>");
}

function alignmentMarker(alignment: T_MarkdownTableAlignment): string {
  switch (alignment) {
    case "left":
      return ":---";
    case "center":
      return ":---:";
    case "right":
      return "---:";
    case null:
      return "---";
  }
}
